import type { ReactNode } from 'react';
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { BrandingProvider } from './theme/BrandingProvider';
import { SessionProvider, useSession } from './auth/SessionProvider';
import { PrivacyProvider } from './components/PrivacyProvider';
import { AppShell } from './components/AppShell';
import { SignInScreen } from './components/SignInScreen';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { SettingsPage } from './pages/SettingsPage';
import { featureNav } from './config/featureNav';

/**
 * Schützt eine Route für rollen-eingegrenzte Nutzer (z. B. Kunde): Ist die Funktion
 * nicht in der Allow-Liste, wird auf das Dashboard umgeleitet. Serverseitig sind die
 * Daten ohnehin gesperrt — dies verhindert nur das direkte Ansteuern per URL.
 */
function Guarded({ pageKey, children }: { pageKey?: string; children: ReactNode }) {
  const { me, loading } = useSession();
  if (loading) return null;
  if (me?.roleRestricted && (!pageKey || !me.allowedFunctions.includes(pageKey))) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function App({ instance }: { instance: IPublicClientApplication }) {
  const items = featureNav.flatMap((section) => section.items);

  return (
    <MsalProvider instance={instance}>
      <BrandingProvider>
        <AuthenticatedTemplate>
          <SessionProvider>
            <PrivacyProvider>
              <BrowserRouter>
                <AppShell>
                  <Routes>
                    {items.map((item) => (
                      <Route
                        key={item.key}
                        path={item.key}
                        element={
                          <Guarded pageKey={item.pageKey}>
                            {item.element ?? <ComingSoonPage title={item.text} description={item.description} />}
                          </Guarded>
                        }
                      />
                    ))}
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AppShell>
              </BrowserRouter>
            </PrivacyProvider>
          </SessionProvider>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <SignInScreen />
        </UnauthenticatedTemplate>
      </BrandingProvider>
    </MsalProvider>
  );
}
